import './new-exercises.scss';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { deleteIcon, downloadIcon, searchIcon } from '../../../assets/images/index';
import Button from '../../../components/button/index.js';
import { useTranslation } from 'react-i18next';
import { NewExerciseService } from "../../../services/new-exercises/new-exercises.service";
import { showErrorToast, showSuccessToast } from "../../../components/Toaster/index";
import CustomDataGrid from "../../../components/data-grid";
import ConfirmationModal from "../../../components/confirmation-modal";


const NewExercisesPage = forwardRef(({ onRowCountChange, fetchCounts }, ref) => {
  const { t } = useTranslation();
  const newExerciseService = new NewExerciseService();

  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(5);
  const [search, setSearch] = React.useState('');
  const [selectedRowIds, setSelectedRowIds] = React.useState([]);
  const [loadingButtons, setLoadingButtons] = useState({});
  const [loadingButtonsV1, setLoadingButtonsV1] = useState({});
  const [searchError, setSearchError] = useState('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [confirmationData, setConfirmationData] = useState({
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const columns = [
    { field: 'exerciseName', headerName: t('exerciseName'), flex: 3, cellClassName: 'custom-cell-right exercise-name-ltr' },
    { field: 'uploadDate', headerName: t('uploadDate'), flex: 1, cellClassName: 'custom-cell-right' },
    {
      field: 'loadToDatabase',
      headerName: t('loadToDatabase'),
      flex: 2.4,
      renderCell: (params) => (
        <div className="custom-cell-loading-btn">
          <Button
            text={t('loadToDatabase')}
            color="blue"
            onClick={() => handleLoadToDBV1(params.row)}
            loader={loadingButtonsV1[params.row.id]}
          />
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: t('operations'),
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton className='action_btns download_btn' onClick={() => handleDownload(params.row)}>
            <img src={downloadIcon} alt="Download" />
          </IconButton>
          <IconButton className='action_btns'
            onClick={() => {
              setConfirmationData({
                title: t('confirmDeleteExercise'),
                message: `${t('deleteExerciseConfirmation')} <strong>${params.row.filename.replace(/\.docx$/i, '')}</strong>`,
                onConfirm: async () => {
                  setConfirmationLoading(true);
                  await handleDelete(params.row);
                  setConfirmationOpen(false);
                  setConfirmationLoading(false);
                },
              });
              setConfirmationOpen(true);
            }}
          >
            <img src={deleteIcon} alt="Delete" />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchExercises();
  }, [page, pageSize]);

  useEffect(() => {
    if (ref?.current?.getRowCount) {
      const count = ref.current.getRowCount();
      console.log('Row count from ref:', count);
    }
  }, [rowCount]);

  const fetchExercises = async (searchTerm = search) => {
    setLoading(true);
    try {
      const data = await newExerciseService.searchExerciseInAzure(page + 1, pageSize, searchTerm);
      const formatted = data.results.map((item, index) => ({
        id: `${item.filename}-${index}`,
        exerciseName: item.filename.replace(/\.docx$/i, ''),
        uploadDate: new Date(item.last_modified).toLocaleDateString('he-IL'),
        filename: item.filename
      }));
  
      setRows(formatted);
      setRowCount(data.total);
      onRowCountChange?.(data.total);
    } catch (error) {
      console.error('Failed to fetch exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    fetchExercises,
    getRowCount: () => rowCount,
  }));

  const handleDownload = async (row) => {
    try {
      const response = await newExerciseService.downloadExerciseFromAzure(row.filename);
  
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
  
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = row.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const handleLoadToDB = async (row) => {
    const rowId = row.id;
    setLoadingButtons(prev => ({ ...prev, [rowId]: true }));
  
    try {
      const res = await newExerciseService.loadExerciseToDB(row.filename);
      if (res.status === 'success') {
        showSuccessToast(`${t('suceessLoadToDatabase')} ${row.filename}`);
        fetchExercises(); // Refresh data grid
        fetchCounts(); // Refresh exercise counters
      } else {
        showErrorToast(res.message || `${t('errorLoadToDatabase')} ${row.filename}`);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || `${t('errorLoadToDatabase')} ${row.filename}`;
      showErrorToast(msg);
    } finally {
      setLoadingButtons(prev => ({ ...prev, [rowId]: false }));
    }
  };


  const handleLoadToDBV1 = async (row) => {
    const rowId = row.id;
    setLoadingButtonsV1(prev => ({ ...prev, [rowId]: true }));
  
    try {
      const res = await newExerciseService.loadExerciseToDBV1(row.filename);
      if (res.status === 'success') {
        showSuccessToast(`${t('suceessLoadToDatabase')} ${row.filename}`);
        fetchExercises();
        fetchCounts();
      } else {
        showErrorToast(res.message || `${t('errorLoadToDatabase')} ${row.filename}`);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || `${t('errorLoadToDatabase')} ${row.filename}`;
      showErrorToast(msg);
    } finally {
      setLoadingButtonsV1(prev => ({ ...prev, [rowId]: false }));
    }
  };


  const handleDelete = async (row) => {
    try {
      const res = await newExerciseService.deleteExerciseFromAzure(row.filename);
      if (res.status === 'success') {
        showSuccessToast(`${t('exerciseDeletedSuccessfully')} ${row.filename}`);
        fetchExercises(); // Refresh data grid
        fetchCounts(); // Refresh exercise counters
      } else {
        showErrorToast(res.message || `${t('errorDeletingExercise')} ${row.filename}`);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || `${t('errorDeletingExercise')} '${row.filename}'`;
      showErrorToast(msg);
    }
  };

  const handleSearch = () => {
    const trimmedSearch = search.trim();
  
    if (trimmedSearch.length === 0) {
      setSearchError('');
      setPage(0);
      fetchExercises();
      return;
    }
  
    if (trimmedSearch.length < 3) {
      setSearchError(t('minCharSearchError'));
      return;
    }
  
    setSearchError('');
    setPage(0);
    fetchExercises(trimmedSearch);
  };

  const handleClear = () => {
    const clearedSearch = '';
    setSearch(clearedSearch);
    setSearchError('');
    setPage(0);
    fetchExercises(clearedSearch);
  };

  return (
    <Box className='dashboard-table'>
      <div className='search-outer-container'>
        <div className='search-container'>
          <TextField
            placeholder={t('searchByExerciseName')}
            variant="outlined"
            size="small"
            value={search}
            error={!!searchError}
            helperText={searchError}
            className='dashboard-search-input'
            onChange={(e) => {
              setSearch(e.target.value);
              if (e.target.value.length >= 3) setSearchError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton>
                    <img src={searchIcon} alt="Search" />
                  </IconButton>
                </InputAdornment>
              ),
              style: { direction: 'rtl' },
            }}
          />
          <Button text={t('search')} color='blue' onClick={handleSearch} />
          <span className="nav-item-text" onClick={handleClear}>{t('clear')}</span>     
        </div>
        <ConfirmationModal
          open={confirmationOpen}
          title={confirmationData.title}
          message={confirmationData.message}
          loading={confirmationLoading}
          onConfirm={confirmationData.onConfirm}
          onCancel={() => setConfirmationOpen(false)}
          confirmText={t('yes')}
          cancelText={t('no')}
        />
      </div>
      <CustomDataGrid
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={(val) => setPage(val)}
        onPageSizeChange={(val) => setPageSize(val)}
        onSelectionChange={(ids) => setSelectedRowIds(ids)}
      />
    </Box>
  )
});

export default NewExercisesPage